/**
 * MemberNotesService - CRUD operations for admin notes about members
 * Note: Returns empty arrays if member_admin_notes table doesn't exist yet
 */

import { supabase } from '@/integrations/supabase/client';
import type { MemberNote, CreateNoteInput, UpdateNoteInput } from '@/types/crm.types';

class MemberNotesServiceClass {
  /**
   * Get all notes for a specific member in a club
   */
  async getNotesByMember(clubId: string, memberId: string): Promise<MemberNote[]> {
    try {
      const { data, error } = await supabase
        .from('member_admin_notes')
        .select('*')
        .eq('club_id', clubId)
        .eq('member_id', memberId)
        .order('created_at', { ascending: false });

      if (error) {
        // Table might not exist yet
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('[MemberNotesService] Table not created yet');
          return [];
        }
        console.error('[MemberNotesService] Error fetching notes:', error);
        return [];
      }

      return (data || []) as MemberNote[];
    } catch (e) {
      console.warn('[MemberNotesService] Notes unavailable:', e);
      return [];
    }
  }

  /**
   * Get recent notes for a member (for lesson prep preview)
   */
  async getRecentNotes(clubId: string, memberId: string, limit: number = 3): Promise<MemberNote[]> {
    try {
      const { data, error } = await supabase
        .from('member_admin_notes')
        .select('*')
        .eq('club_id', clubId)
        .eq('member_id', memberId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return [];
      }

      return (data || []) as MemberNote[];
    } catch (e) {
      return [];
    }
  }

  /**
   * Create a new note
   */
  async createNote(input: CreateNoteInput): Promise<MemberNote> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      throw new Error('User not authenticated');
    }

    // Build insert object with only core fields
    const insertData: any = {
      club_id: input.club_id,
      member_id: input.member_id,
      admin_id: userData.user.id,
      content: input.content
    };

    // Only include new fields if they're explicitly provided (not undefined)
    // This makes the service backward-compatible if migration hasn't run yet
    if (input.visible_to_player !== undefined) {
      insertData.visible_to_player = input.visible_to_player;
    }
    if (input.is_private !== undefined) {
      insertData.is_private = input.is_private;
    }
    if (input.solo_round_id !== undefined && input.solo_round_id !== null) {
      insertData.solo_round_id = input.solo_round_id;
    }
    if (input.multiplayer_game_id !== undefined && input.multiplayer_game_id !== null) {
      insertData.multiplayer_game_id = input.multiplayer_game_id;
    }
    if (input.event_id !== undefined && input.event_id !== null) {
      insertData.event_id = input.event_id;
    }
    if (input.event_round_id !== undefined && input.event_round_id !== null) {
      insertData.event_round_id = input.event_round_id;
    }

    let { data, error } = await supabase
      .from('member_admin_notes')
      .insert(insertData)
      .select('*')
      .single();

    // If error is about missing columns (migration not applied), retry with core fields only
    if (error && (error.message?.includes('column') || error.code === 'PGRST204')) {
      console.warn('[MemberNotesService] Migration may not be applied, retrying with core fields only');
      const coreData = {
        club_id: input.club_id,
        member_id: input.member_id,
        admin_id: userData.user.id,
        content: input.content
      };
      
      const retryResult = await supabase
        .from('member_admin_notes')
        .insert(coreData)
        .select('*')
        .single();
      
      if (retryResult.error) {
        console.error('[MemberNotesService] Error creating note:', retryResult.error);
        throw retryResult.error;
      }
      
      data = retryResult.data;
      error = null;
    } else if (error) {
      console.error('[MemberNotesService] Error creating note:', error);
      throw error;
    }

    return data as MemberNote;
  }

  /**
   * Update an existing note
   */
  async updateNote(input: UpdateNoteInput): Promise<MemberNote> {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (input.content !== undefined) {
      updateData.content = input.content;
    }
    if (input.visible_to_player !== undefined) {
      updateData.visible_to_player = input.visible_to_player;
    }
    if (input.is_private !== undefined) {
      updateData.is_private = input.is_private;
    }
    if (input.solo_round_id !== undefined) {
      updateData.solo_round_id = input.solo_round_id;
    }
    if (input.multiplayer_game_id !== undefined) {
      updateData.multiplayer_game_id = input.multiplayer_game_id;
    }
    if (input.event_id !== undefined) {
      updateData.event_id = input.event_id;
    }
    if (input.event_round_id !== undefined) {
      updateData.event_round_id = input.event_round_id;
    }

    const { data, error } = await supabase
      .from('member_admin_notes')
      .update(updateData)
      .eq('id', input.id)
      .select('*')
      .single();

    if (error) {
      console.error('[MemberNotesService] Error updating note:', error);
      throw error;
    }

    return data as MemberNote;
  }

  /**
   * Delete a note
   */
  async deleteNote(noteId: string): Promise<void> {
    const { error } = await supabase
      .from('member_admin_notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      console.error('[MemberNotesService] Error deleting note:', error);
      throw error;
    }
  }

  /**
   * Get note count for a member
   */
  async getNoteCount(clubId: string, memberId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('member_admin_notes')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .eq('member_id', memberId);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Get all visible notes for a member (for player view)
   */
  async getVisibleNotesForMember(clubId: string, memberId: string): Promise<MemberNote[]> {
    try {
      // Verify user is authenticated and matches memberId
      const { data: userData, error: authError } = await supabase.auth.getUser();
      if (authError || !userData.user) {
        console.warn('[MemberNotesService] User not authenticated:', authError);
        return [];
      }

      if (userData.user.id !== memberId) {
        console.warn('[MemberNotesService] Member ID mismatch:', {
          memberId,
          authUserId: userData.user.id
        });
        return [];
      }

      // Get notes - admin_id references auth.users, so we'll fetch profiles separately
      const { data, error } = await supabase
        .from('member_admin_notes')
        .select('*')
        .eq('club_id', clubId)
        .eq('member_id', memberId)
        .eq('visible_to_player', true)
        .eq('is_private', false)
        .order('created_at', { ascending: false });

      if (error) {
        // Log detailed error information
        console.error('[MemberNotesService] Error fetching visible notes:', {
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          clubId,
          memberId,
          authUserId: userData.user.id
        });

        // Check if it's a schema issue (migration not applied)
        if (error.code === 'PGRST204' || error.message?.includes('column') || error.message?.includes('does not exist')) {
          console.warn('[MemberNotesService] Schema columns may not exist - migration may not be applied');
          return [];
        }

        // Check if it's an RLS policy issue
        if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('policy')) {
          console.error('[MemberNotesService] RLS policy may be blocking access. Check policy configuration.');
          return [];
        }

        return [];
      }

      console.log('[MemberNotesService] Fetched visible notes:', {
        count: data?.length || 0,
        clubId,
        memberId,
        authUserId: userData.user.id
      });

      // Fetch admin profiles for the notes
      const adminIds = [...new Set((data || []).map((note: any) => note.admin_id).filter(Boolean))];
      const adminProfiles: Record<string, { full_name: string | null; avatar_url: string | null }> = {};

      if (adminIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', adminIds);

        if (!profilesError && profiles) {
          profiles.forEach((profile: any) => {
            adminProfiles[profile.id] = {
              full_name: profile.display_name,
              avatar_url: profile.avatar_url
            };
          });
        } else if (profilesError) {
          console.warn('[MemberNotesService] Error fetching admin profiles:', profilesError);
        }
      }

      // Transform notes with admin data
      const notes = (data || []).map((note: any) => ({
        ...note,
        admin: adminProfiles[note.admin_id] ? {
          full_name: adminProfiles[note.admin_id].full_name || 'Coach',
          avatar_url: adminProfiles[note.admin_id].avatar_url
        } : undefined
      })) as MemberNote[];

      return notes;
    } catch (e) {
      console.error('[MemberNotesService] Exception fetching visible notes:', e);
      return [];
    }
  }

  /**
   * Get count of visible notes for a member (for badge)
   */
  async getVisibleNoteCount(clubId: string, memberId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('member_admin_notes')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .eq('member_id', memberId)
        .eq('visible_to_player', true)
        .eq('is_private', false);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (e) {
      return 0;
    }
  }
}

export const MemberNotesService = new MemberNotesServiceClass();
